/**
 * AI CONTEXT: business-logic - Subscription state and mock billing
 * Journal: J-20250809-01
 * 
 * Purpose:
 * - Manage user subscriptions and bonus entries
 * - Simulate billing operations for MVP
 * - Persist to localStorage for continuity
 * 
 * Future considerations:
 * - Integrate real payment gateway
 * - Webhooks to adjust credits and billing dates
 * - Handle proration and plan changes
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import { authorizedFetch, isApiConfigured } from '../services/apiClient';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// Mock subscription data moved outside component
const mockSubscriptions = [
  {
    id: '1',
    userId: '1',
    status: 'active',
    plan: 'monthly',
    amount: 24.99,
    startDate: '2024-01-15',
    nextBillingDate: '2024-02-15',
    autoEntryEnabled: true,
    bonusEntries: 5,
    paymentMethod: 'authorize_net'
  }
];

export const SubscriptionProvider = ({ children }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, authChecked } = useAuth();

  useEffect(() => {
    const load = async () => {
      // Wait until auth status is determined to avoid premature 401s in Safari
      if (!authChecked) return;
      
      // Safari-specific delay to allow token generation to complete
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isSafari && isAuthenticated) {
        const delay = isMobile ? 2000 : 1000; // Mobile Safari needs longer delay
        if (process.env.NODE_ENV !== 'production') {
          console.log(`🔍 SubscriptionContext: Safari detected, delaying subscription API call by ${delay}ms to allow token generation`);
        }
        
        setTimeout(() => {
          loadSubscriptionData();
        }, delay);
      } else {
        loadSubscriptionData();
      }
    };
    
    const loadSubscriptionData = async () => {
      // Prefer server subscription when API configured and authenticated; else fall back to demo/local
      if (isApiConfigured() && isAuthenticated) {
        try {
          const sub = await authorizedFetch('/my/subscription', { method: 'GET' });
          // Handle empty object response (no subscription)
          if (sub && Object.keys(sub).length === 0) {
            setSubscriptions([]);
            return;
          }
          setSubscriptions(sub ? [sub] : []);
          return;
        } catch (_) {
          // fall through to local
        }
      }
      const savedSubscriptions = localStorage.getItem('tolbert_subscriptions');
      if (savedSubscriptions) {
        setSubscriptions(JSON.parse(savedSubscriptions));
      } else {
        setSubscriptions(mockSubscriptions);
      }
    };
    
    load();
  }, [isAuthenticated, authChecked]);

  useEffect(() => {
    // Save subscriptions to localStorage
    localStorage.setItem('tolbert_subscriptions', JSON.stringify(subscriptions));
  }, [subscriptions]);

  const createSubscription = async (userId, plan = 'monthly') => {
    setLoading(true);
    
    try {
      if (isApiConfigured()) {
        const amount = 24.99;
        const interval = plan === 'monthly' ? 'month' : 'year';
        const payload = { plan, amount, interval, paymentMethod: 'authorize_net' };
        // Attempt direct subscription endpoint (prefers Stripe Checkout if configured)
        try {
          const created = await authorizedFetch('/subscriptions', { method: 'POST', body: JSON.stringify(payload) });
          if (created && created.url) {
            window.location.assign(created.url);
            return { success: true, redirect: true };
          }
          // If server responded without URL (local/demo path)
          if (created && created.status === 'active') {
            setSubscriptions(prev => [...prev, { ...created, userId }]);
            toast.success('Subscription activated successfully!');
            return { success: true, subscription: created };
          }
        } catch (e) {
          // fall through to Checkout Session fallback
        }
        // Fallback: create a Stripe Checkout Session via payments endpoint (subscription mode)
        try {
          const origin = window.location.origin;
          const successUrl = `${origin}/dashboard?tab=subscription&status=success&session_id={CHECKOUT_SESSION_ID}`;
          const cancelUrl = `${origin}/dashboard?tab=subscription&status=cancelled`;
          const body = {
            items: [{ name: `Tolbert Premium (${plan})`, price: amount, quantity: 1, tier: 'Bronze', interval }],
            subscription: true,
            successUrl,
            cancelUrl,
          };
          const session = await authorizedFetch('/payments/checkout-session', { method: 'POST', body: JSON.stringify(body) });
          if (session && session.url) {
            window.location.assign(session.url);
            return { success: true, redirect: true };
          }
        } catch (e2) {
          // bubble to outer toast
        }
        toast.error('Failed to create subscription. Please try again.');
        return { success: false };
      }
      // Fallback: local demo mode
      const newSubscription = {
        id: Date.now().toString(),
        userId,
        status: 'active',
        plan,
        amount: 24.99,
        startDate: new Date().toISOString().split('T')[0],
        nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        autoEntryEnabled: true,
        bonusEntries: 5,
        paymentMethod: 'authorize_net'
      };
      setSubscriptions(prev => [...prev, newSubscription]);
      toast.success('Subscription activated (demo mode)');
      return { success: true, subscription: newSubscription };
    } catch (error) {
      toast.error('Failed to create subscription. Please try again.');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async (subscriptionId) => {
    setLoading(true);
    
    try {
      if (isApiConfigured()) {
        await authorizedFetch('/subscriptions/cancel', { 
          method: 'POST',
          body: JSON.stringify({ subscriptionId })
        });
        // Reload from API
        try {
          const sub = await authorizedFetch('/my/subscription', { method: 'GET' });
          // Handle empty object response (no subscription)
          if (sub && Object.keys(sub).length === 0) {
            setSubscriptions([]);
          } else {
            setSubscriptions(sub ? [sub] : []);
          }
        } catch (_) {}
        toast.success('Subscription cancelled successfully');
        return { success: true };
      }
      // Demo fallback
      setSubscriptions(prev =>
        prev.map(sub =>
          sub.id === subscriptionId
            ? { ...sub, status: 'cancelled', cancelledDate: new Date().toISOString().split('T')[0] }
            : sub
        )
      );
      toast.success('Subscription cancelled (demo mode)');
      return { success: true };
    } catch (error) {
      toast.error('Failed to cancel subscription');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const updateSubscription = (subscriptionId, updates) => {
    setSubscriptions(prev =>
      prev.map(sub =>
        sub.id === subscriptionId ? { ...sub, ...updates } : sub
      )
    );
  };

  const getUserSubscription = (userId) => {
    // When API is configured, subscriptions array contains only the current user's subscription
    // (loaded from /my/subscription endpoint), so we can safely return the first active one
    // For mock/local mode, we need to match by userId
    if (isApiConfigured()) {
      // API returns only the current user's subscription, so find any active subscription
      return subscriptions.find(sub => sub.status === 'active') || null;
    }
    // In mock mode, match by userId
    return subscriptions.find(sub => sub.userId === userId && sub.status === 'active');
  };

  const isUserSubscribed = (userId) => {
    const subscription = getUserSubscription(userId);
    return !!(subscription && subscription.status === 'active');
  };

  const getBonusEntries = (userId) => {
    const subscription = getUserSubscription(userId);
    return subscription ? subscription.bonusEntries : 0;
  };

  const processRecurringBilling = async () => {
    setLoading(true);
    
    try {
      // Simulate processing recurring payments
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const today = new Date().toISOString().split('T')[0];
      
      setSubscriptions(prev =>
        prev.map(sub => {
          if (sub.status === 'active' && sub.nextBillingDate === today) {
            // Process payment and update next billing date
            const nextBillingDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            return {
              ...sub,
              nextBillingDate,
              lastBillingDate: today
            };
          }
          return sub;
        })
      );
      
      toast.success('Recurring billing processed successfully');
    } catch (error) {
      toast.error('Failed to process recurring billing');
    } finally {
      setLoading(false);
    }
  };

  const toggleAutoEntry = (subscriptionId, enabled) => {
    updateSubscription(subscriptionId, { autoEntryEnabled: enabled });
    toast.success(`Auto-entry ${enabled ? 'enabled' : 'disabled'}`);
  };

  const addBonusEntries = (userId, amount) => {
    const subscription = getUserSubscription(userId);
    if (subscription) {
      updateSubscription(subscription.id, {
        bonusEntries: subscription.bonusEntries + amount
      });
      toast.success(`${amount} bonus entries added!`);
    }
  };

  const refreshSubscription = useCallback(async () => {
    if (!isApiConfigured()) return null;
    try {
      const sub = await authorizedFetch('/my/subscription', { method: 'GET' });
      if (sub && Object.keys(sub).length === 0) {
        setSubscriptions([]);
        return null;
      }
      setSubscriptions(sub ? [sub] : []);
      return sub;
    } catch (error) {
      console.warn('Failed to refresh subscription:', error);
      return null;
    }
  }, []);

  const value = {
    subscriptions,
    loading,
    createSubscription,
    cancelSubscription,
    updateSubscription,
    getUserSubscription,
    isUserSubscribed,
    getBonusEntries,
    processRecurringBilling,
    toggleAutoEntry,
    addBonusEntries,
    refreshSubscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}; 